/**
 * AudioReviewCard - Card 1: Browse & Apply Generated Audio (OUTPUT)
 *
 * Compact, collapsible design with consistent typography:
 * - Header with play button, version picker, duration - all inline
 * - Expandable to show transcript and apply button
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// Lottie player type declaration for TypeScript (inline lottie-player used)
import {
  Loader2,
  RefreshCw,
  Sparkles,
  Check,
  AlertCircle,
  ChevronDown,
  Mic2,
  Volume2,
} from 'lucide-react';
import type { SlideAudioVersion, AppliedVersionInfo, WordTiming } from '@/services/audioService';
import type { AgentJobProgress } from '@/services/agentJobService';


interface AudioReviewCardProps {
  versions: SlideAudioVersion[];
  currentVersion: SlideAudioVersion | null;
  appliedVersion: AppliedVersionInfo | null;
  onVersionChange: (version: number) => void;
  audioSrc: string | null;
  audioRef: React.RefObject<HTMLAudioElement>;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
  onRestart: () => void;
  onTimeUpdate: () => void;
  onEnded: () => void;
  onLoadedMetadata: () => void;
  onCanPlayThrough: () => void;
  onAudioError: (e: React.SyntheticEvent<HTMLAudioElement>) => void;
  wordTimings: WordTiming[] | null;
  currentWordIndex: number;
  onWordClick: (time: number) => void;
  script: string;
  /** When undefined, the Apply Section is hidden entirely (used to gate beta-only controls). */
  onApply?: () => void;
  isApplying?: boolean;
  applyProgress?: AgentJobProgress | null;
  onCancelApply?: () => void;
  applyError?: string | null;
  audioLoadError?: string | null;
  isLoadingAudio?: boolean;
  className?: string;
}

// Format time as m:ss
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

// Live Transcript Component
const LiveTranscript: React.FC<{
  script: string;
  wordTimings: WordTiming[] | null;
  currentWordIndex: number;
  onWordClick?: (time: number) => void;
}> = ({ script, wordTimings, currentWordIndex, onWordClick }) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const wordRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    if (currentWordIndex >= 0 && wordRef.current && scrollRef.current) {
      wordRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentWordIndex]);

  if (!wordTimings || wordTimings.length === 0) {
    return (
      <div className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
        {script || 'No script available'}
      </div>
    );
  }

  return (
    <div className="h-[56px] rounded-md bg-muted/30 overflow-hidden" ref={scrollRef}>
      <ScrollArea className="h-full">
        <div className="px-2.5 py-2">
          <p className="text-[11px] leading-relaxed m-0" style={{ wordWrap: 'break-word' }}>
            {wordTimings.map((wt, idx) => (
              <span
                key={idx}
                ref={idx === currentWordIndex ? wordRef : undefined}
                onClick={() => onWordClick?.(wt.startTime)}
                className={cn(
                  'cursor-pointer transition-colors rounded px-0.5',
                  idx === currentWordIndex
                    ? 'bg-primary text-primary-foreground font-medium'
                    : 'hover:bg-muted-foreground/10'
                )}
              >
                {wt.word}{' '}
              </span>
            ))}
          </p>
        </div>
      </ScrollArea>
    </div>
  );
};

export const AudioReviewCard: React.FC<AudioReviewCardProps> = ({
  versions,
  currentVersion,
  appliedVersion,
  onVersionChange,
  audioSrc,
  audioRef,
  isPlaying,
  currentTime,
  duration,
  onPlayPause,
  onSeek,
  onRestart: _onRestart,
  onTimeUpdate,
  onEnded,
  onLoadedMetadata,
  onCanPlayThrough,
  onAudioError,
  wordTimings,
  currentWordIndex,
  onWordClick,
  script,
  onApply,
  isApplying,
  applyProgress,
  onCancelApply,
  applyError,
  audioLoadError,
  isLoadingAudio,
  className,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);

  // Compute states
  const isVersionMismatch = React.useMemo(() => {
    if (!appliedVersion || !currentVersion) return false;
    return appliedVersion.id !== currentVersion.id;
  }, [appliedVersion, currentVersion]);

  const hasNoAppliedAudio = !appliedVersion && currentVersion?.status === 'COMPLETED';
  const displayVersion = currentVersion || versions[0];

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className={cn('rounded-lg border border-border/50 bg-card overflow-hidden', className)}>
        {/* Hidden audio element */}
        {audioSrc && !isLoadingAudio && (
          <audio
            ref={audioRef}
            src={audioSrc}
            onTimeUpdate={onTimeUpdate}
            onEnded={onEnded}
            onLoadedMetadata={onLoadedMetadata}
            onCanPlayThrough={onCanPlayThrough}
            onError={onAudioError}
          />
        )}

        {/* Header Row */}
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-primary" />
              <span className="text-xs font-medium">Audio Preview</span>
              {/* Status Badge */}
              {isVersionMismatch && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-amber-500/50 text-amber-600 dark:text-amber-400 whitespace-nowrap">
                  v{currentVersion?.version}
                </Badge>
              )}
              {hasNoAppliedAudio && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-primary/50 text-primary dark:text-accent-foreground whitespace-nowrap">
                  Ready
                </Badge>
              )}
              {appliedVersion && !isVersionMismatch && !hasNoAppliedAudio && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-green-500/50 text-green-600 dark:text-green-400 whitespace-nowrap">
                  Applied
                </Badge>
              )}
            </div>
            <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform', isExpanded && 'rotate-180')} />
          </button>
        </CollapsibleTrigger>

        {/* Expandable Content */}
        <CollapsibleContent>
          <div className="px-3 pb-2.5 space-y-2.5">
            {/* Player Row: Play | Progress | Time | Version */}
            <div className="flex items-center gap-2">
              {/* Play/Pause */}
              <button
                onClick={(e) => { e.stopPropagation(); onPlayPause(); }}
                disabled={isLoadingAudio || !audioSrc}
                className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors flex-shrink-0 disabled:opacity-50"
              >
                {isLoadingAudio ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isPlaying ? (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Progress Bar */}
              <div
                className="flex-1 h-1.5 bg-muted rounded-full cursor-pointer group"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  onSeek((x / rect.width) * duration);
                }}
              >
                <div
                  className="h-full bg-primary rounded-full relative transition-all"
                  style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>

              {/* Time */}
              <span className="text-[11px] tabular-nums text-muted-foreground flex-shrink-0">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>

              {/* Version Picker */}
              {versions.length > 1 && (
                <Select
                  value={String(displayVersion?.version || 1)}
                  onValueChange={(v) => onVersionChange(Number(v))}
                >
                  <SelectTrigger
                    className="h-7 w-auto min-w-[48px] px-2 text-xs border-border/50 bg-background/80"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <SelectValue>
                      <span className="font-medium">v{displayVersion?.version}</span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent align="end" className="min-w-[200px]">
                    {versions.slice().sort((a, b) => b.version - a.version).map((v) => (
                      <SelectItem key={v.id} value={String(v.version)} className="py-2">
                        <div className="flex items-center justify-between w-full gap-3">
                          <span className={cn('font-medium text-xs', v.isCurrentVersion && 'text-primary')}>
                            v{v.version}
                          </span>
                          <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
                            <Mic2 className="h-3.5 w-3.5" />
                            <span className="truncate max-w-[70px]">{v.voiceName}</span>
                            <span className="tabular-nums">{formatTime(v.audioDuration || 0)}</span>
                          </span>
                          {appliedVersion?.id === v.id && (
                            <Check className="h-3.5 w-3.5 text-green-600" />
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Audio Load Error */}
            {audioLoadError && (
              <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 rounded-md px-2.5 py-1.5">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{audioLoadError}</span>
              </div>
            )}

            {/* Live Transcript */}
            <LiveTranscript
              script={script}
              wordTimings={wordTimings}
              currentWordIndex={currentWordIndex}
              onWordClick={onWordClick}
            />

            {/* Apply Section - Inline button with thinking indicator */}
            {onApply && (
              <div className="flex items-center gap-3">
                {/* Apply Button - Compact */}
                <Button
                  variant={hasNoAppliedAudio || isVersionMismatch ? 'default' : 'outline'}
                  size="sm"
                  onClick={onApply}
                  disabled={isApplying}
                  className="h-8 px-3 flex-shrink-0"
                >
                  {isApplying ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      <span className="text-xs">Syncing</span>
                    </>
                  ) : isVersionMismatch ? (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      <span className="text-xs">Update v{currentVersion?.version}</span>
                    </>
                  ) : hasNoAppliedAudio ? (
                    <>
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                      <span className="text-xs">Apply</span>
                    </>
                  ) : (
                    <>
                      <Check className="h-3.5 w-3.5 mr-1.5" />
                      <span className="text-xs">Applied</span>
                    </>
                  )}
                </Button>

                {/* Thinking Indicator - Shows when applying */}
                {isApplying && applyProgress ? (
                  <div className="flex-1 min-w-0 flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/15">
                    <div className="flex-shrink-0 w-5 h-5 rounded overflow-hidden bg-white/50 dark:bg-black/20 flex items-center justify-center">
                      <lottie-player
                        src="https://assets-v2.lottiefiles.com/a/d5543bc0-a8a5-11ee-a44f-7bc5f99a0a60/g1j2POcLI9.json"
                        background="transparent"
                        speed="1"
                        style={{ width: '16px', height: '16px' }}
                        loop
                        autoplay
                      />
                    </div>
                    <p className="flex-1 text-[11px] text-foreground/80 truncate">
                      {applyProgress.thinking || applyProgress.currentOperation || 'Syncing...'}
                    </p>
                    {onCancelApply && (
                      <button
                        onClick={onCancelApply}
                        className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                        title="Cancel"
                      >
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ) : (
                  /* Status text when not applying */
                  <span className="text-[11px] text-muted-foreground">
                    {appliedVersion ? `v${appliedVersion.version} synced` : 'Not synced'}
                  </span>
                )}
              </div>
            )}

            {/* Error */}
            {onApply && applyError && (
              <p className="text-xs text-destructive text-center">{applyError}</p>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default AudioReviewCard;
